import { Link } from 'react-router-dom'
import JumpNav from '../components/JumpNav.jsx'
import FaqBlock from '../components/FaqBlock.jsx'
import Related from '../components/Related.jsx'
import Seo from '../components/Seo.jsx'
import LastUpdated from '../components/LastUpdated.jsx'
import { faqLd } from '../lib/faq.js'

const COA_FAQ = [
  {
    q: 'What does “98% purity” on a COA mean?',
    a: 'It means the HPLC analysis found the target peptide made up ~98% of the peptide content in the sample, with the rest being related impurities (truncated or modified sequences). For research peptides, purity in the high-90s is generally considered good.',
  },
  {
    q: 'What’s the difference between HPLC and MS on a COA?',
    a: 'HPLC (high-performance liquid chromatography) measures purity — how much of the sample is the intended peptide. Mass spectrometry (MS) confirms identity — that the molecule’s measured mass matches the expected molecular weight of that peptide. A good COA shows both.',
  },
  {
    q: 'Can a COA be faked?',
    a: 'Yes. A PDF is easy to copy, reuse, or edit. Trust is higher when the COA is tied to a specific batch/lot number, comes from a named third-party lab, and can be verified with that lab directly — not just a generic image reused across products.',
  },
  {
    q: 'Does a COA make a peptide safe to use?',
    a: 'No. A COA speaks to identity and purity of the sample tested — not to whether a compound is safe or appropriate for you. It does not replace medical guidance, and many research peptides are not approved for human use.',
  },
]

function Section({ id, title, children }) {
  return (
    <section className="article-section" id={id}>
      <h2>{title}</h2>
      {children}
    </section>
  )
}

export default function CoaGuide() {
  const jump = [
    { id: 'what', label: 'What is a COA' },
    { id: 'why', label: 'Why it matters' },
    { id: 'hplc', label: 'HPLC purity' },
    { id: 'ms', label: 'Mass spec' },
    { id: 'checklist', label: 'What to look for' },
    { id: 'redflags', label: 'Red flags' },
    { id: 'faq', label: 'FAQ' },
  ]

  return (
    <main className="page">
      <Seo
        title="How to Read a Peptide COA | CalculateMyPeptides"
        description="A plain-language guide to reading a peptide Certificate of Analysis: what HPLC purity and mass spectrometry mean, what a legitimate COA includes, and the red flags of a fake."
        jsonLd={faqLd('How to read a peptide COA — FAQ', COA_FAQ)}
        image="/og/guide.png"
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Guides', path: '/guides' },
          { name: 'How to Read a COA', path: '/how-to-read-a-coa' },
        ]}
      />
      <div className="container page-narrow article">
        <span className="pill blue" style={{ marginBottom: 10 }}>Guide</span>
        <h1 className="section-title">How to Read a Peptide COA (Certificate of Analysis)</h1>
        <p className="section-sub" style={{ maxWidth: '64ch' }}>
          A COA is the lab report that tells you what’s actually in a vial — how pure it is and whether it’s the
          right molecule. Here’s how to read one, what the numbers mean, and how to tell a real one from a
          decorative PDF.
        </p>
        <LastUpdated />

        <JumpNav items={jump} />

        <Section id="what" title="What is a COA?">
          <p>
            A <strong>Certificate of Analysis</strong> is a document from an analytical lab reporting the results of
            testing a specific batch of a peptide. At minimum it identifies the compound, states its measured
            purity, and confirms its identity — usually with two techniques: <strong>HPLC</strong> for purity and{' '}
            <strong>mass spectrometry</strong> for identity.
          </p>
        </Section>

        <Section id="why" title="Why it matters">
          <p>
            Peptides are sold as research chemicals with little oversight, so the COA is often the only objective
            evidence of what you have. It answers two questions: <em>is this the right molecule</em> (identity), and{' '}
            <em>how much of the sample is that molecule</em> (purity). Everything downstream — your{' '}
            <Link to="/calculator">dose calculation</Link>, your expectations — assumes the vial contains what the
            label says. The COA is what backs that assumption.
          </p>
        </Section>

        <Section id="hplc" title="HPLC purity — the percentage">
          <p>
            HPLC (high-performance liquid chromatography) separates the sample into its components and measures how
            much of it is the target peptide versus related impurities (truncated or modified sequences). The result
            is a <strong>purity percentage</strong> — you’ll typically see something like 98%+. The COA usually
            includes the <strong>chromatogram</strong>: a graph with a tall main peak (your peptide) and small
            side peaks (impurities). A single dominant peak is what you want to see.
          </p>
        </Section>

        <Section id="ms" title="Mass spectrometry — the identity check">
          <p>
            HPLC tells you the sample is pure; it doesn’t prove it’s the <em>right</em> peptide. That’s what mass
            spectrometry (MS) is for. MS measures the molecule’s mass and compares it to the expected{' '}
            <strong>molecular weight</strong> for that peptide. If the measured mass matches the theoretical mass,
            the identity is confirmed. A complete COA shows both the HPLC purity and the MS identity.
          </p>
        </Section>

        <Section id="checklist" title="What a legitimate COA includes">
          <ul className="pep-list">
            <li>The <strong>peptide name and sequence</strong>, and its expected molecular weight.</li>
            <li>A <strong>batch or lot number</strong> that ties the report to a specific production run.</li>
            <li><strong>HPLC purity %</strong> with the chromatogram image.</li>
            <li><strong>Mass spec</strong> result confirming the molecular weight.</li>
            <li>The <strong>testing lab’s name</strong> and the test date.</li>
          </ul>
        </Section>

        <Section id="redflags" title="Red flags of a fake or reused COA">
          <ul className="pep-list warn-list">
            <li>No batch/lot number, or the same COA image used across many different products.</li>
            <li>Purity only, with no mass-spec identity confirmation (or vice versa).</li>
            <li>No named lab, or a lab that can’t be contacted to verify the report.</li>
            <li>A molecular weight on the report that doesn’t match the peptide’s known weight.</li>
            <li>Blurry, cropped, or edited-looking chromatograms.</li>
          </ul>
        </Section>

        <div id="faq">
          <FaqBlock title="COA FAQ" items={COA_FAQ} />
        </div>

        <div className="pep-disclaimer" style={{ marginTop: 22 }}>
          ⚠️ Educational only — not medical advice. A COA reports on the sample tested; it does not make any compound
          safe or appropriate for you. Many research peptides are not approved for human use.
        </div>

        <Related
          title="Related"
          links={[
            { to: '/peptide-dosage-guide', label: 'Peptide dosage guide', desc: 'Reconstitution & dosing' },
            { to: '/glossary', label: 'Glossary', desc: 'HPLC, lyophilized, and more' },
            { to: '/calculator', label: 'Peptide calculator', desc: 'Once you trust the vial' },
            { to: '/guides', label: 'All guides', desc: 'Back to the guides index' },
          ]}
        />
      </div>
    </main>
  )
}
